import { useMemo } from 'react';

import { CookieService } from '~/services/actions/cookie-actions';
import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from '~/services/actions/tracking-actions';
import { type OddsFormat } from '~/types';

import { type OddsUtils } from '../types';

export const useOddsUtils = (): OddsUtils => {
	const cookieService = useMemo(() => new CookieService(), []);
	const trackingActions = useMemo(() => new TrackingActions(), []);

	const changeOddsFormat = (newOddsFormat: OddsFormat): void => {
		const cookie = cookieService.select('odds_format');

		cookie.set(newOddsFormat, new Date().getTime() + 86400000);

		trackingActions.eventTracking({
			event: TRACKING_EVENTS.CHANGE_ODDS_FORMAT,
			eventCategory: TRACKING_CATEGORIES.ODDS_FORMAT,
			eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.CHANGE_ODDS_FORMAT],
			eventLabel: newOddsFormat,
		});

		// TODO: CI-1135 replace hard refresh with revalidate
		window.location.reload();
	};

	const convertAmericanToFractional = (oddsAmerican: string): string => {
		const oddsDecimal = convertAmericanToDecimal(oddsAmerican);

		return convertDecimalOddsToFractional(oddsDecimal);
	};

	const convertAmericanToDecimal = (oddsAmerican: string): number => {
		const numericalValue = Number(oddsAmerican);
		const decimal =
			numericalValue >= 0 ? 1 + numericalValue / 100 : 1 - 100 / numericalValue;
		const fixedDecimal = ((decimal * 100) / 100).toFixed(4);

		return Number(fixedDecimal);
	};

	const convertFractionalToAmerican = (oddsFractional: string): string => {
		const oddsDecimal = convertFractionalToDecimal(oddsFractional);

		return convertDecimalToAmerican(oddsDecimal);
	};

	const convertFractionalToDecimal = (fractionalOdds: string): number => {
		const fractionParts = fractionalOdds.split('/');
		if (fractionParts.length < 2) return 0;

		const decimal = Number(fractionParts[0]) / Number(fractionParts[1]) + 1;
		const fixedDecimal = (Math.round(decimal * 100) / 100).toFixed(2);

		return Number(fixedDecimal);
	};

	const convertDecimalToAmerican = (oddsDecimal: number): string => {
		let convertedOddsToUS;
		if (oddsDecimal >= 2) {
			convertedOddsToUS = (oddsDecimal - 1) * 100;
			return `${convertedOddsToUS.toFixed(0)}`;
		}
		if (oddsDecimal < 2) {
			convertedOddsToUS = -100 / (oddsDecimal - 1);
			return convertedOddsToUS.toFixed(0);
		}

		return '';
	};

	const convertDecimalOddsToFractional = (oddsDecimal: number): string => {
		if (oddsDecimal > 3) {
			return `${Math.floor(oddsDecimal - 1)}/1`;
		}
		const reduce = (numerator: number, denominator: number) => {
			// NOTE: this relic from legacy seems to be
			// euclidean greatest common denominator
			let gcd: any = function gcd(a: number, b: number): number {
				return b ? gcd(b, a % b) : a;
			};
			gcd = gcd(numerator, denominator);
			return [numerator / gcd, denominator / gcd];
		};
		const temp: any = oddsDecimal - 1;
		const number = Number(Number(temp.toFixed(2) * 100).toFixed(2));
		const simplestForm = reduce(number, 100);

		if (simplestForm.length < 2) {
			return '';
		}

		return `${simplestForm[0]}/${simplestForm[1]}`;
	};

	return {
		changeOddsFormat,
		convertAmericanToFractional,
		convertAmericanToDecimal,
		convertFractionalToAmerican,
		convertFractionalToDecimal,
		convertDecimalToAmerican,
		convertDecimalOddsToFractional,
	};
};
